import { FieldSet } from "@prismicio/editor-ui"
import {
  isGroupContent,
  isNestableContent,
  type SimpleSliceContent,
} from "@prismicio/types-internal/lib/content"
import type { LegacySlice } from "@prismicio/types-internal/lib/customtypes"
import type { ReactNode } from "react"

import { getFieldLabel } from "../fields/utils"
import { getGroupLabel, GroupEditor } from "../GroupEditor"
import { NestableWidgetSwitch } from "../NestableWidgetSwitch"
import { getSliceSelectionDOMId, useSliceSelection } from "../SliceSelection"
import { maybeResetSliceWidget } from "./utils"

interface LegacySliceEditorProps {
  content: SimpleSliceContent
  id: string
  legacySlice: LegacySlice
  onContentChange: (content: SimpleSliceContent) => void
  readOnly: boolean
  sliceKey?: string
  header?: ReactNode
}

export function LegacySliceEditor(props: LegacySliceEditorProps) {
  const {
    content,
    legacySlice,
    sliceKey,
    id,
    readOnly,
    onContentChange,
    header,
  } = props

  const selection = useSliceSelection()

  function onFocus() {
    if (!sliceKey) return
    selection.update({ id: sliceKey, requestedFrom: "sliceFieldSet" })
  }

  const sliceLegend =
    legacySlice.type === "Group"
      ? getGroupLabel(legacySlice)
      : getFieldLabel(legacySlice)

  return (
    <FieldSet
      id={sliceKey && getSliceSelectionDOMId("sliceFieldSet", sliceKey)}
      selected={sliceKey ? selection.isSelected(sliceKey) : false}
      onFocus={onFocus}
      legend={sliceLegend}
      header={header}
    >
      {legacySlice.type === "Group" ? (
        <GroupEditor
          id={id}
          readOnly={readOnly}
          onContentChange={onContentChange}
          group={legacySlice}
          content={isGroupContent(content) ? content : undefined}
        />
      ) : (
        <NestableWidgetSwitch
          id={id}
          readOnly={readOnly}
          nestableWidget={legacySlice}
          content={isNestableContent(content) ? content : undefined}
          onContentChange={(nestableContent) => {
            const widget = maybeResetSliceWidget({ field: legacySlice })
            if (!nestableContent && widget) {
              onContentChange(widget)
            }

            if (!nestableContent) return
            onContentChange(nestableContent)
          }}
        />
      )}
    </FieldSet>
  )
}
